import { useCallback, useState } from 'react'
import { useAuthSession } from '../../lib/authSession'
import type { DocumentRef } from '../../lib/documentReader'
import RecordEditForm from '../recordEdits/RecordEditForm'
import { scopeKey, type EditScope, type EditTarget } from '../recordEdits/contract'
import { useRead } from './useRead'

/** The same editor for a current object, including objects with no Position.
 * The preview still admits the exact target and chosen Fund/client on the server. */
export default function ObjectRecordEditor({
  target,
  scope,
  documents = [],
  onRecordChanged,
  onBusyChange,
}: {
  target: EditTarget
  scope: EditScope
  documents?: DocumentRef[]
  onRecordChanged?: () => void
  onBusyChange?: (busy: boolean) => void
}) {
  const session = useAuthSession()
  const key = `${session}:${target.kind}:${target.id}:${scopeKey(scope)}`
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [startedKey, setStartedKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const reportBusy = useCallback(
    (next: boolean) => {
      setBusy(next)
      onBusyChange?.(next)
    },
    [onBusyChange]
  )
  const capability = useRead<{ record_edits_enabled?: boolean }>('/api/navigation/capabilities')
  if (capability.value?.record_edits_enabled !== true) return null
  const open = openKey === key
  return (
    <div className="record-edit-target-section" data-testid="object-record-editor">
      <button
        type="button"
        className="vbtn"
        aria-expanded={open}
        disabled={busy}
        onClick={() => {
          setStartedKey(key)
          setOpenKey(open ? null : key)
        }}
      >
        {open ? 'Close editor' : 'Edit Record'}
      </button>
      {startedKey === key ? (
        <div hidden={!open}>
          <RecordEditForm
            key={key}
            target={target}
            scope={scope}
            documents={documents}
            onRecordChanged={onRecordChanged}
            onBusyChange={reportBusy}
          />
        </div>
      ) : null}
    </div>
  )
}
